// Shared tweet processing utilities
// Extracted from TwitterImporter to be used by both single and bulk generation

// Extract tweet ID from various URL formats
export const extractTweetIdFromUrl = (url) => {
  const patterns = [
    /twitter\.com\/\w+\/status\/(\d+)/,
    /x\.com\/\w+\/status\/(\d+)/,
    /mobile\.twitter\.com\/\w+\/status\/(\d+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Fetch tweet data via API
export const fetchTweetData = async (tweetId) => {
  try {
    const apiUrl = `/api/twitter/tweet`;

    console.log(`Fetching tweet from: ${apiUrl}`);

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tweetId }),
    });

    if (!response.ok) {
      const raw = await response.text();
      let details = raw;
      try {
        const parsed = JSON.parse(raw);
        details = parsed.error ? `${parsed.error}: ${parsed.details || ''}` : raw;
      } catch (_) {
        // keep raw text
      }
      console.error('Netlify function error details:', details);
      throw new Error(`Netlify Function Error: ${response.status}${details ? ` - ${details}` : ''}`);
    }

    const data = await response.json();
    console.log('API Response:', data);
    console.log('🔍 Full API Response structure:', JSON.stringify(data, null, 2));
    return data;
    
  } catch (error) {
    console.error('API call failed:', error.message);
    throw error;
  }
};

// Download profile picture and convert to File object
export const downloadProfilePicture = async (imageUrl) => {
  try {
    // Upgrade to high quality profile picture
    const highResUrl = imageUrl.replace('_normal', '_400x400');
    console.log('Using high-res profile picture:', highResUrl);

    const apiUrl = `/api/download-image`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ imageUrl: highResUrl }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    const blob = await response.blob();
    return new File([blob], 'profile.jpg', { type: blob.type });

  } catch (error) {
    console.error('Profile picture download failed:', error);
    return null;
  }
};

// Process tweet data into the format expected by TweetGenerator
// This is the EXACT same logic used in TwitterImporter.generateVideoFromTweet
export const processTweetData = async (tweetUrl, audioSelection) => {
  // Extract tweet ID
  const tweetId = extractTweetIdFromUrl(tweetUrl);
  console.log('Extracted tweet ID from URL:', { url: tweetUrl, tweetId });
  if (!tweetId) {
    throw new Error('Invalid Twitter URL format');
  }

  // Fetch tweet data
  const tweetData = await fetchTweetData(tweetId);
  
  // Download profile picture
  const profilePicture = await downloadProfilePicture(tweetData.user.profile_image_url);
  
  // Clean up tweet text by removing t.co URLs if media is present
  let cleanedText = tweetData.text;
  console.log('Original tweet text:', tweetData.text);
  console.log('Media data:', tweetData.extendedEntities?.media);
  
  if (tweetData.extendedEntities?.media?.length > 0) {
    // Remove all t.co URLs that correspond to media
    tweetData.extendedEntities.media.forEach(media => {
      console.log('Removing URL:', media.url);
      cleanedText = cleanedText.replace(media.url, '').trim();
    });
    console.log('Cleaned tweet text:', cleanedText);
  }
  
  // Extract all media URLs from the tweet
  const mediaUrls = tweetData.extendedEntities?.media?.map(media => media.media_url_https) || [];
  console.log('Extracted media URLs:', mediaUrls);

  // Check for video content and extract video data
  let videoData = null;
  let hasVideo = false;
  
  console.log('🔍 Checking for video content...');
  console.log('extendedEntities:', tweetData.extendedEntities);
  
  if (tweetData.extendedEntities?.media?.length > 0) {
    console.log('📱 Media found, checking each item...');
    
    tweetData.extendedEntities.media.forEach((media, index) => {
      console.log(`Media ${index}:`, {
        type: media.type,
        hasVideoInfo: !!media.video_info,
        url: media.url
      });
    });
    
    const videoMedia = tweetData.extendedEntities.media.find(media => media.type === 'video');
    
    if (videoMedia && videoMedia.video_info) {
      hasVideo = true;
      console.log('🎥 Video detected in tweet!');
      console.log('Video info:', videoMedia.video_info);
      
      // Extract the highest quality video variant
      const variants = videoMedia.video_info.variants || [];
      const mp4Variants = variants.filter(variant => variant.content_type === 'video/mp4');
      
      console.log('🎬 Available MP4 variants:', mp4Variants.map(v => ({
        bitrate: v.bitrate,
        url: v.url.substring(0, 80) + '...'
      })));
      
      if (mp4Variants.length > 0) {
        // Sort by bitrate (highest first) and get the best quality
        const bestVariant = mp4Variants.sort((a, b) => (b.bitrate || 0) - (a.bitrate || 0))[0];
        
        videoData = {
          url: bestVariant.url,
          duration: videoMedia.video_info.duration_millis / 1000, // Convert to seconds
          aspectRatio: videoMedia.video_info.aspect_ratio,
          bitrate: bestVariant.bitrate,
          width: videoMedia.original_info?.width,
          height: videoMedia.original_info?.height,
          thumbnailUrl: videoMedia.media_url_https
        };
        
        console.log('🎬 Selected video variant:', {
          url: videoData.url,
          duration: videoData.duration,
          bitrate: videoData.bitrate,
          resolution: `${videoData.width}x${videoData.height}`
        });
        
        // Override audio selection to use video audio instead
        console.log('🔊 Video detected - using video audio instead of selected audio');
        audioSelection = {
          type: 'video',
          videoData: videoData
        };
      } else {
        console.log('⚠️ No MP4 variants found in video');
      }
    } else {
      console.log('📷 No video found, treating as image tweet');
    }
  } else {
    console.log('📷 No media found in tweet');
  }

  // For video tweets, exclude video media from tweet display so server can overlay video on top
  let displayMediaUrls = mediaUrls;
  if (hasVideo && tweetData.extendedEntities?.media) {
    displayMediaUrls = tweetData.extendedEntities.media
      .filter(media => media.type !== 'video') // Exclude videos from tweet display
      .map(media => media.media_url_https);
    console.log('📺 Video tweet: excluding video from tweet display, will overlay video on server:', displayMediaUrls);
  }

  // Process quoted tweet if it exists
  let processedQuotedTweet = null;
  if (tweetData.quoted_tweet) {
    console.log('Processing quoted tweet media...', tweetData.quoted_tweet);
    
    // Extract media URLs from quoted tweet - check multiple possible locations
    let quotedMediaUrls = [];
    
    // Check extendedEntities first (standard location)
    if (tweetData.quoted_tweet.extendedEntities?.media) {
      quotedMediaUrls = tweetData.quoted_tweet.extendedEntities.media.map(media => media.media_url_https);
    }
    // Also check entities.media as fallback
    else if (tweetData.quoted_tweet.entities?.media) {
      quotedMediaUrls = tweetData.quoted_tweet.entities.media.map(media => media.media_url_https);
    }
    
    console.log('Quoted tweet media URLs:', quotedMediaUrls);
    console.log('Original quoted tweet text:', tweetData.quoted_tweet.text);
    
    // Clean up quoted tweet text by removing ALL t.co URLs (not just media ones)
    let quotedCleanedText = tweetData.quoted_tweet.text;
    
    // Remove specific media URLs if they exist
    if (tweetData.quoted_tweet.extendedEntities?.media) {
      tweetData.quoted_tweet.extendedEntities.media.forEach(media => {
        if (media.url) {
          quotedCleanedText = quotedCleanedText.replace(media.url, '').trim();
        }
      });
    }
    
    // Also check entities.media for URLs to remove
    if (tweetData.quoted_tweet.entities?.media) {
      tweetData.quoted_tweet.entities.media.forEach(media => {
        if (media.url) {
          quotedCleanedText = quotedCleanedText.replace(media.url, '').trim();
        }
      });
    }
    
    // Fallback: Remove any remaining t.co links that might represent media
    // This handles cases where media URLs aren't properly categorized
    if (quotedMediaUrls.length > 0) {
      quotedCleanedText = quotedCleanedText.replace(/https:\/\/t\.co\/[a-zA-Z0-9]+/g, '').trim();
    }
    
    // Additional aggressive cleanup: Remove any orphaned t.co URLs at the end of text
    // This handles cases where the link is at the end and represents media
    quotedCleanedText = quotedCleanedText.replace(/\s*https:\/\/t\.co\/[a-zA-Z0-9]+\s*$/, '').trim();
    
    console.log('Cleaned quoted tweet text:', quotedCleanedText);
    
    processedQuotedTweet = {
      ...tweetData.quoted_tweet,
      text: quotedCleanedText, // Use cleaned text
      mediaUrls: quotedMediaUrls // Add media URLs to quoted tweet
    };
  }

  // Format data for the tweet generator
  const processedData = {
    name: tweetData.user.name,
    username: tweetData.user.username,
    message: cleanedText,
    verified: tweetData.user.verified,
    profilePicture: profilePicture,
    metrics: tweetData.metrics || {},
    created_at: tweetData.created_at,
    lang: tweetData.lang,
    is_reply: tweetData.is_reply,
    extendedEntities: tweetData.extendedEntities,
    mediaUrls: displayMediaUrls, // URLs to display in tweet overlay (excludes videos)
    originalMediaUrls: mediaUrls, // All original media URLs for reference
    audioSelection: audioSelection, // Add audio selection
    quoted_tweet: processedQuotedTweet, // Add processed quoted tweet with media URLs
    url: tweetUrl, // Add original URL for reference
    hasVideo: hasVideo, // Add video detection flag
    videoData: videoData // Add video data if present
  };

  return processedData;
};

// Download video from Twitter URL and convert to File object
export const downloadVideo = async (videoUrl) => {
  try {
    console.log('Downloading video from:', videoUrl);

    const apiUrl = `/api/download-image`; // Reuse the existing download endpoint

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ imageUrl: videoUrl }), // The endpoint can handle video URLs too
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch video: ${response.status}`);
    }

    const blob = await response.blob();
    return new File([blob], 'video.mp4', { type: 'video/mp4' });

  } catch (error) {
    console.error('Video download failed:', error);
    return null;
  }
}; 